import React from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { formatOrderOption } from '@/utils/orderFormatLine';

type OrderTextValue = string | number | {
  code?: string | number;
  name?: string;
  label?: string;
} | null | undefined;

export interface OrderAutocompleteItem {
  id: string | number;
  code?: OrderTextValue;
  order_code?: OrderTextValue;
  customer_name?: OrderTextValue;
  customer?: OrderTextValue;
  total_amount?: string | number | null;
}

interface OrderAutocompleteOption {
  id: string;
  label: string;
  displayLabel: string;
  order: OrderAutocompleteItem;
  statusColor?: string;
  line1Color?: string;
}

export interface OrderAutocompleteProps {
  orders?: OrderAutocompleteItem[];
  value?: string | null;
  onChange: (orderId: string | null, order?: OrderAutocompleteItem | null) => void;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  loading?: boolean;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  filterOptions?: (options: OrderAutocompleteOption[]) => OrderAutocompleteOption[];
  noOptionsText?: React.ReactNode;
  sx?: any;
  variant?: 'standard' | 'outlined' | 'filled';
  error?: boolean;
  helperText?: React.ReactNode;
}

const formatTextValue = (value: OrderTextValue) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);

  const code = typeof value.code === 'number' ? String(value.code) : value.code;
  return code || value.name || value.label || '';
};

const buildOrderOption = (order: OrderAutocompleteItem): OrderAutocompleteOption => {
  const formatted = formatOrderOption(order as any);
  const code = formatted.displayLabel || formatTextValue(order.code) || formatTextValue(order.order_code) || String(order.id);

  return {
    id: String(order.id),
    label: code,
    displayLabel: code,
    order,
    statusColor: formatted.statusColor,
    line1Color: formatted.line1Color,
  };
};

const OrderAutocomplete: React.FC<OrderAutocompleteProps> = ({
  orders = [],
  value = null,
  onChange,
  placeholder = '',
  disabled = false,
  fullWidth = true,
  size = 'small',
  loading = false,
  inputValue,
  onInputChange,
  filterOptions,
  noOptionsText,
  sx,
  variant,
  error = false,
  helperText,
}) => {
  const orderOptions = React.useMemo(
    () => orders.map(buildOrderOption),
    [orders]
  );
  const selectedOption = React.useMemo(
    () => orderOptions.find((option) => option.id === value) || null,
    [orderOptions, value]
  );
  const displayInputValue =
    inputValue !== undefined && inputValue !== ''
      ? inputValue
      : selectedOption?.displayLabel || inputValue;

  return (
    <Autocomplete
      options={orderOptions}
      fullWidth={fullWidth}
      disabled={disabled}
      loading={loading}
      inputValue={displayInputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          onInputChange?.(newInputValue || '');
        }
      }}
      filterOptions={filterOptions}
      noOptionsText={noOptionsText}
      sx={sx}
      getOptionLabel={(option) => option.displayLabel}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      value={selectedOption}
      onChange={(_, newValue) => {
        onChange(newValue?.id || null, newValue?.order || null);
      }}
      size={size}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          fullWidth={fullWidth}
          size={size}
          variant={variant}
          error={error}
          helperText={helperText}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              color: option.line1Color || option.statusColor || '#0f172a',
            }}
          >
            {option.label}
          </Typography>
        </Box>
      )}
    />
  );
};

export default OrderAutocomplete;
